import { friendlyDbError } from '../utils/friendlyDbError';
/**
 * Label Store — Manages wh_label_templates + wh_label_prints + wh_printer_configs
 */

import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from './referenceDataCache';
import type {
  WhLabelTemplate, WhLabelTemplateFormData, WhLabelPrint,
  WhPrinterConfig, WhPrinterConfigFormData,
} from '../types/manufacturing';

interface LabelStore {
  templates: WhLabelTemplate[];
  printerConfigs: WhPrinterConfig[];
  loading: boolean;
  error: string | null;

  fetchTemplates: () => Promise<void>;
  createTemplate: (data: WhLabelTemplateFormData) => Promise<WhLabelTemplate | null>;
  updateTemplate: (id: string, data: Partial<WhLabelTemplateFormData>) => Promise<WhLabelTemplate | null>;
  deleteTemplate: (id: string) => Promise<boolean>;
  recordPrint: (templateId: string, lotId: string, copies?: number) => Promise<WhLabelPrint | null>;

  fetchPrinterConfigs: () => Promise<void>;
  createPrinterConfig: (data: WhPrinterConfigFormData) => Promise<WhPrinterConfig | null>;
  updatePrinterConfig: (id: string, data: Partial<WhPrinterConfigFormData>) => Promise<WhPrinterConfig | null>;
  deletePrinterConfig: (id: string) => Promise<boolean>;

  clearError: () => void;
}

export const useLabelStore = create<LabelStore>((set, get) => ({
  templates: [],
  printerConfigs: [],
  loading: false,
  error: null,

  // ── Templates ──────────────────────────────────────────────

  fetchTemplates: async () => {
    set({ loading: true, error: null });
    try {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId) throw new Error('Organizzazione non trovata');

      const leggi = (select: string) => supabase
        .from('wh_label_templates')
        .select(select)
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('name');

      // Il prodotto collegato arriva da 20260910e. Se la migration non c'e'
      // ancora PostgREST non conosce la relazione: si ricarica senza, invece di
      // lasciare la sezione etichette vuota.
      let { data, error } = await leggi('*, printer_config:wh_printer_configs(*), product:products(id, name, sku)');
      if (error) {
        ({ data, error } = await leggi('*, printer_config:wh_printer_configs(*)'));
      }

      if (error) throw error;
      set({ templates: (data as unknown as WhLabelTemplate[]) || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createTemplate: async (formData) => {
    set({ loading: true, error: null });
    try {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId) throw new Error('Organizzazione non trovata');
      const { data: userData } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('wh_label_templates')
        .insert({
          organization_id: orgId,
          name: formData.name,
          format: formData.format,
          field_config: formData.field_config,
          is_default: formData.is_default || false,
          qr_access: formData.qr_access || 'authenticated',
          notes: formData.notes || null,
          printer_config_id: formData.printer_config_id || null,
          product_id: formData.product_id || null,
          created_by: userData?.user?.id || null,
        })
        .select('*, printer_config:wh_printer_configs(*), product:products(id, name, sku)')
        .single();

      if (error) throw error;
      set({ templates: [...get().templates, data].sort((a, b) => a.name.localeCompare(b.name)) });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    } finally {
      set({ loading: false });
    }
  },

  updateTemplate: async (id, formData) => {
    set({ loading: true, error: null });
    try {
      const updateData: Record<string, unknown> = { ...formData };
      // Ensure printer_config_id null is sent explicitly
      if ('printer_config_id' in formData && formData.printer_config_id === null) {
        updateData.printer_config_id = null;
      }
      // Il collegamento al prodotto si toglie, non solo si cambia
      if ('product_id' in formData && !formData.product_id) {
        updateData.product_id = null;
      }
      // Remove the joined relations from the update payload
      delete updateData.printer_config;
      delete updateData.product;

      const { data, error } = await supabase
        .from('wh_label_templates')
        .update(updateData)
        .eq('id', id)
        .select('*, printer_config:wh_printer_configs(*), product:products(id, name, sku)')
        .single();

      if (error) throw error;
      set({ templates: get().templates.map(t => t.id === id ? data : t) });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    } finally {
      set({ loading: false });
    }
  },

  deleteTemplate: async (id) => {
    try {
      const { error } = await supabase.from('wh_label_templates').update({ is_active: false }).eq('id', id);
      if (error) return false;
      set({ templates: get().templates.filter(t => t.id !== id) });
      return true;
    } catch { return false; }
  },

  recordPrint: async (templateId, lotId, copies = 1) => {
    try {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId) return null;
      const { data: userData } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('wh_label_prints')
        .insert({
          organization_id: orgId,
          template_id: templateId,
          lot_id: lotId,
          printed_by: userData?.user?.id || null,
          copies,
        })
        .select()
        .single();

      if (error) return null;
      return data;
    } catch { return null; }
  },

  // ── Printer Configs ────────────────────────────────────────

  fetchPrinterConfigs: async () => {
    try {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId) return;

      const { data, error } = await supabase
        .from('wh_printer_configs')
        .select('*')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      set({ printerConfigs: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  },

  createPrinterConfig: async (formData) => {
    set({ error: null });
    try {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId) throw new Error('Organizzazione non trovata');
      const { data: userData } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('wh_printer_configs')
        .insert({
          organization_id: orgId,
          name: formData.name,
          model: formData.model || null,
          paper_width_mm: formData.paper_width_mm,
          paper_height_mm: formData.paper_height_mm,
          orientation: formData.orientation || 'portrait',
          is_default: formData.is_default || false,
          notes: formData.notes || null,
          created_by: userData?.user?.id || null,
        })
        .select()
        .single();

      if (error) throw error;
      set({ printerConfigs: [...get().printerConfigs, data].sort((a, b) => a.name.localeCompare(b.name)) });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updatePrinterConfig: async (id, formData) => {
    set({ error: null });
    try {
      const { data, error } = await supabase
        .from('wh_printer_configs')
        .update(formData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      set({ printerConfigs: get().printerConfigs.map(p => p.id === id ? data : p) });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deletePrinterConfig: async (id) => {
    try {
      const { error } = await supabase.from('wh_printer_configs').update({ is_active: false }).eq('id', id);
      if (error) return false;
      set({ printerConfigs: get().printerConfigs.filter(p => p.id !== id) });
      return true;
    } catch { return false; }
  },

  clearError: () => set({ error: null }),
}));
